<script setup>
import { ref, watch } from 'vue'
import { request } from '../api/request'
import { getToken } from '../api/session'
import PageState from '../components/feedback/PageState.vue'
const props = defineProps({ venueId: { type: [String, Number], required: true }, gameId: { type: Number, default: 0 } })
const emit = defineEmits(['login'])
const games = ref([]),
  venue = ref(null),
  loading = ref(false),
  error = ref(''),
  page = ref(1),
  total = ref(0),
  launching = ref(false)
let version = 0
async function load() {
  const current = ++version,
    token = getToken()
  games.value = []
  venue.value = null
  error.value = ''
  if (!token) return
  loading.value = true
  try {
    const [venues, response] = await Promise.all([
      request('/game/venue/list'),
      request('/game/page', {
        method: 'POST',
        data: { venueId: Number(props.venueId), pageNumber: page.value, pageSize: 20 },
      }),
    ])
    if (current !== version || token !== getToken()) return
    venue.value = (venues || []).find((item) => String(item.id) === String(props.venueId))
    games.value = response?.data || []
    total.value = Number(response?.total || 0)
  } catch (e) {
    if (current === version) error.value = e.message
  } finally {
    if (current === version) loading.value = false
  }
}
async function launch(game) {
  if (!getToken()) {
    emit('login')
    return
  }
  if (launching.value) return
  launching.value = true
  error.value = ''
  try {
    const data = await request(`/game/venue/${props.venueId}/launch`, {
      method: 'POST',
      data: { id: Number(props.venueId), gameId: Number(game?.id || 0) },
    })
    const target = new URL(data?.launchUrl || data?.url || '')
    if (!['http:', 'https:'].includes(target.protocol)) throw new Error('场馆地址无效')
    window.location.assign(target.href)
  } catch (e) {
    error.value = e.message
  } finally {
    launching.value = false
  }
}
watch(
  () => [props.venueId, getToken()],
  () => {
    page.value = 1
    load()
  },
  { immediate: true },
)
watch(page, load)
</script>
<template>
  <section class="container advertising-venue">
    <h2>{{ venue?.name || '场馆游戏' }}</h2>
    <PageState v-if="!getToken()" title="登录后查看场馆" description="登录后查看场馆目录与游戏信息"><button type="button" class="primary" @click="emit('login')">登录查看场馆</button></PageState>
    <PageState v-else-if="loading" loading />
    <PageState v-else-if="error" :error="error" @retry="load" />
    <template v-else>
      <button v-if="Number.isSafeInteger(gameId) && gameId > 0" class="primary" :disabled="launching" @click="launch({ id: gameId })">进入推荐游戏</button>
      <div class="advertising-venue-games">
        <button v-for="game in games" :key="game.id" :disabled="launching" @click="launch(game)">
          <img
            v-if="game.imageUrl || game.iconUrl"
            :src="game.imageUrl || game.iconUrl"
            :alt="game.name"
          /><span>{{ game.name }}</span>
        </button>
      </div>
      <PageState v-if="!games.length" :title="venue ? '该场馆暂无可用游戏' : '该场馆暂未开放'" description="你可以稍后再来查看" />
      <div v-if="total > 20" class="advertising-venue-pagination">
        <button :disabled="page <= 1" @click="page--">上一页</button><span>第 {{ page }} 页</span
        ><button :disabled="page * 20 >= total" @click="page++">下一页</button>
      </div>
    </template>
  </section>
</template>
<style scoped>
.advertising-venue {
  min-height: calc(100dvh - 280px);
  padding: 112px 0 64px;
}
.advertising-venue > h2 { margin: 0 0 24px; color: #303442; font-size: 28px; font-weight: 600; line-height: 1.5; }
.advertising-venue :deep(.page-state) { min-height: 340px; border: 1px solid #e0e9f3; border-radius: 16px; background: #fff; }
.advertising-venue-pagination { display: flex; justify-content: flex-end; align-items: center; flex-wrap: wrap; gap: 16px; margin-top: 24px; color: #6e7f98; font-size: 14px; }
.advertising-venue-pagination button { min-height: 42px; padding: 10px 16px; border: 1px solid #dce6f0; border-radius: 8px; color: #415b79; background: #fff; }
.advertising-venue button:focus-visible { outline: 3px solid #80b8e5; outline-offset: 3px; }
.advertising-venue button:disabled { opacity: .5; cursor: not-allowed; }
.advertising-venue-games {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 18px;
  margin: 20px 0;
}
.advertising-venue-games button {
  background: white;
  border: 1px solid #dce5f1;
  border-radius: 12px;
  overflow: hidden;
  padding: 12px;
}
.advertising-venue-games img {
  width: 100%;
  height: 120px;
  object-fit: contain;
}
.advertising-venue-games span {
  display: block;
  padding-top: 12px;
}
@media (max-width: 999px) { .advertising-venue { padding-top: 100px; } }
@media (max-width: 600px) { .advertising-venue { padding-top: 92px; padding-bottom: 32px; } .advertising-venue > h2 { font-size: 24px; } .advertising-venue-games { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; } }
</style>
